import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';

const PAGE_SIZE = 20;

const STATUS_COLORS = {
  SENDING: '#facc15',
  COMPLETED: theme.colors.success,
  FAILED: theme.colors.error,
};

const inputStyle = {
  width: '100%', padding: '9px 12px',
  backgroundColor: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '7px', color: theme.colors.text,
  fontSize: '0.9rem', boxSizing: 'border-box', outline: 'none',
};

function Marketing({ token, companySaldo }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [successBanner, setSuccessBanner] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [form, setForm] = useState({ name: '', template_id: '', contact_list_id: '' });
  const [contactCount, setContactCount] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [lists, setLists] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');
  const [insufficient, setInsufficient] = useState(null);
  const [leyHorario, setLeyHorario] = useState(null);
  // null = aún no se ha cargado la tarifa desde la BD (price_general). No usamos
  // un valor quemado: si no se puede leer, se bloquea el envío.
  const [pricePerMessage, setPricePerMessage] = useState(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchData = () => {
    setLoading(true);
    fetch(`/api/marketing?page=${page}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { data: [], total: 0 })
      .then(d => { setData(d.data || []); setTotal(d.total || 0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [page, token]);

  const openModal = async () => {
    setModalStep(1);
    setForm({ name: '', template_id: '', contact_list_id: '' });
    setContactCount(0);
    setModalError('');
    const [tRes, lRes, pRes] = await Promise.all([
      fetch('/api/templates?page=1', { headers: { Authorization: `Bearer ${token}` } }),
      fetch('/api/marketing/lists', { headers: { Authorization: `Bearer ${token}` } }),
      fetch('/api/marketing/price', { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    const tData = tRes.ok ? await tRes.json() : { data: [] };
    const lData = lRes.ok ? await lRes.json() : [];
    const pData = pRes.ok ? await pRes.json() : null;
    setTemplates((tData.data || []).filter(t => t.status === 'APPROVED'));
    setLists(lData);
    // Tarifa real de la empresa (price_general.price_conversation). Si no se
    // pudo leer, queda en null y el envío se bloquea con un aviso.
    const price = pData && pData.price_conversation != null ? Number(pData.price_conversation) : null;
    setPricePerMessage(Number.isFinite(price) && price > 0 ? price : null);
    setShowModal(true);
  };

  const handleFormNext = async () => {
    if (!form.name.trim()) return setModalError('El nombre es requerido');
    if (!form.template_id) return setModalError('Selecciona una plantilla');
    if (!form.contact_list_id) return setModalError('Selecciona una lista de contactos');
    setModalError('');
    const res = await fetch(
      `/api/marketing/contact-count?contact_list_id=${form.contact_list_id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const d = res.ok ? await res.json() : { count: 0 };
    setContactCount(d.count);
    setModalStep(3);
  };

  const handleSend = async () => {
    setSubmitting(true);
    setModalError('');
    try {
      const res = await fetch('/api/marketing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: form.name,
          template_id: Number(form.template_id),
          contact_list_id: Number(form.contact_list_id),
        }),
      });
      const d = await res.json();
      if (!res.ok) {
        if (d.insufficientBalance) {
          setShowModal(false);
          setInsufficient(d);
          return;
        }
        if (d.leyHorario) {
          setShowModal(false);
          setLeyHorario(d);
          return;
        }
        return setModalError(d.message || 'Error al enviar la campaña');
      }
      setShowModal(false);
      setSuccessBanner('La campaña se está enviando. Consulta los resultados dentro de 10 minutos.');
      setTimeout(() => setSuccessBanner(''), 8000);
      fetchData();
    } catch {
      setModalError('Error de conexión');
    } finally {
      setSubmitting(false);
    }
  };

  const [exportingId, setExportingId] = useState(null);

  const handleExport = async (row) => {
    setExportingId(row.id);
    try {
      const res = await fetch(`/api/marketing/${row.id}/logs/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = String(row.name || 'campana').replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 60);
      a.download = `publicidad_${safeName}_${row.id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert('No se pudo exportar el CSV. Intenta de nuevo.');
    } finally {
      setExportingId(null);
    }
  };

  const estimatedCost = pricePerMessage != null ? contactCount * pricePerMessage : 0;
  const saldoActual = companySaldo ? parseFloat(companySaldo.saldo_final) : 0;

  const StatusBadge = ({ status }) => (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: '20px',
      fontSize: '0.75rem', fontWeight: '700',
      backgroundColor: `${STATUS_COLORS[status] || theme.colors.textMuted}22`,
      color: STATUS_COLORS[status] || theme.colors.textMuted,
      border: `1px solid ${STATUS_COLORS[status] || theme.colors.textMuted}`,
    }}>{status}</span>
  );

  return (
    <main style={{ padding: isMobile ? '1rem' : '2rem 1.5rem', maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: isMobile ? '1.25rem' : '2rem', boxShadow: `0 4px 24px ${theme.colors.glowSubtle}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h1 style={{ color: theme.colors.primary, fontSize: '1.4rem', margin: 0 }}>Publicidad</h1>
          <button onClick={openModal} style={{ padding: '9px 20px', backgroundColor: theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: '7px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer', width: isMobile ? '100%' : 'auto' }}>+ Crear</button>
        </div>

        {successBanner && (
          <div style={{ backgroundColor: 'rgba(74,222,128,0.1)', border: `1px solid ${theme.colors.success}`, borderRadius: '8px', padding: '0.75rem 1rem', color: theme.colors.success, fontSize: '0.9rem', marginBottom: '1.5rem' }}>
            {successBanner}
          </div>
        )}

        {loading ? (
          <p style={{ color: theme.colors.textMuted, textAlign: 'center', padding: '2rem' }}>Cargando...</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr>
                  {['Nombre', 'Plantilla', 'Lista', 'Total', 'Enviados', 'Fallidos', 'Estado', 'Fecha', ''].map(col => (
                    <th key={col} style={{ textAlign: 'left', padding: '10px 10px', borderBottom: `1px solid ${theme.colors.border}`, color: theme.colors.primary, fontWeight: '600', whiteSpace: 'nowrap' }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: theme.colors.textMuted }}>Sin campañas. Crea la primera.</td></tr>
                ) : data.map(row => (
                  <tr key={row.id} style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                    <td style={{ padding: '9px 10px', color: theme.colors.text, fontWeight: '500' }}>{row.name}</td>
                    <td style={{ padding: '9px 10px', color: theme.colors.textMuted }}>{row.template_name || '—'}</td>
                    <td style={{ padding: '9px 10px', color: theme.colors.textMuted }}>{row.list_name || '—'}</td>
                    <td style={{ padding: '9px 10px', color: theme.colors.text }}>{row.total_contacts}</td>
                    <td style={{ padding: '9px 10px', color: theme.colors.success }}>{row.sent_count}</td>
                    <td style={{ padding: '9px 10px', color: theme.colors.error }}>{row.failed_count}</td>
                    <td style={{ padding: '9px 10px' }}><StatusBadge status={row.status} /></td>
                    <td style={{ padding: '9px 10px', color: theme.colors.textMuted, whiteSpace: 'nowrap' }}>{new Date(row.created_at).toLocaleDateString('es-CO')}</td>
                    <td style={{ padding: '9px 10px' }}>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button onClick={() => navigate(`/publicidad/${row.id}`)} style={{ padding: '5px 12px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '6px', color: theme.colors.text, cursor: 'pointer', fontSize: '0.8rem' }}>Ver</button>
                        <button onClick={() => handleExport(row)} disabled={exportingId === row.id} style={{ padding: '5px 12px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.primary}`, borderRadius: '6px', color: theme.colors.primary, cursor: exportingId === row.id ? 'not-allowed' : 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{exportingId === row.id ? '...' : 'Exportar'}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1.2rem' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '6px 16px', borderRadius: '6px', border: `1px solid ${theme.colors.border}`, cursor: page === 1 ? 'not-allowed' : 'pointer', backgroundColor: 'transparent', color: page === 1 ? theme.colors.textMuted : theme.colors.text, fontSize: '0.88rem' }}>← Anterior</button>
          <span style={{ color: theme.colors.textMuted, fontSize: '0.88rem' }}>Página {page} de {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '6px 16px', borderRadius: '6px', border: `1px solid ${theme.colors.border}`, cursor: page === totalPages ? 'not-allowed' : 'pointer', backgroundColor: 'transparent', color: page === totalPages ? theme.colors.textMuted : theme.colors.text, fontSize: '0.88rem' }}>Siguiente →</button>
        </div>
      </div>

      {showModal && (
        <div onClick={() => { if (!submitting) setShowModal(false); }} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: '2rem', width: '100%', maxWidth: '440px', boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}>

            {modalStep === 1 && (
              <>
                <h2 style={{ color: '#facc15', fontSize: '1.2rem', marginBottom: '1rem' }}>⚠️ Aviso de cobro</h2>
                <p style={{ color: theme.colors.text, fontSize: '0.95rem', lineHeight: '1.7', marginBottom: '1.5rem' }}>
                  {pricePerMessage != null
                    ? <>Se cobrará <strong style={{ color: theme.colors.primary }}>${pricePerMessage.toLocaleString('es-CO')} pesos colombianos</strong> por cada mensaje enviado en este módulo de publicidad.</>
                    : <span style={{ color: theme.colors.error }}>No se pudo cargar la tarifa configurada para tu empresa. Intenta de nuevo más tarde o contacta a soporte.</span>}
                </p>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, cursor: 'pointer', fontSize: '0.9rem' }}>Cancelar</button>
                  <button onClick={() => setModalStep(2)} disabled={pricePerMessage == null} style={{ flex: 1, padding: '10px', backgroundColor: pricePerMessage == null ? theme.colors.border : theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, cursor: pricePerMessage == null ? 'not-allowed' : 'pointer', fontSize: '0.9rem', fontWeight: '700' }}>Continuar</button>
                </div>
              </>
            )}

            {modalStep === 2 && (
              <>
                <h2 style={{ color: theme.colors.primary, fontSize: '1.2rem', marginBottom: '1.5rem' }}>Nueva campaña</h2>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.85rem', marginBottom: '0.3rem' }}>Nombre</label>
                  <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.85rem', marginBottom: '0.3rem' }}>Plantilla (solo aprobadas)</label>
                  <select value={form.template_id} onChange={e => setForm(f => ({ ...f, template_id: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="">— Seleccionar plantilla —</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.85rem', marginBottom: '0.3rem' }}>Lista de contactos</label>
                  <select value={form.contact_list_id} onChange={e => setForm(f => ({ ...f, contact_list_id: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="">— Seleccionar lista —</option>
                    {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                {modalError && <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>{modalError}</p>}
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, cursor: 'pointer', fontSize: '0.9rem' }}>Cancelar</button>
                  <button onClick={handleFormNext} style={{ flex: 1, padding: '10px', backgroundColor: theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, cursor: 'pointer', fontSize: '0.9rem', fontWeight: '700' }}>Enviar inmediatamente</button>
                </div>
              </>
            )}

            {modalStep === 3 && (
              <>
                <h2 style={{ color: theme.colors.primary, fontSize: '1.2rem', marginBottom: '1.5rem' }}>¿Estás seguro?</h2>
                <div style={{ backgroundColor: theme.colors.background, borderRadius: '10px', padding: '1rem', marginBottom: '1.5rem' }}>
                  <p style={{ color: theme.colors.text, fontSize: '0.95rem', margin: '0 0 0.6rem' }}>Se enviarán <strong style={{ color: theme.colors.primary }}>{contactCount.toLocaleString('es-CO')}</strong> mensajes.</p>
                  <p style={{ color: theme.colors.text, fontSize: '0.95rem', margin: '0 0 0.6rem' }}>Costo estimado: <strong style={{ color: '#facc15' }}>${estimatedCost.toLocaleString('es-CO')} pesos</strong></p>
                  <p style={{ color: theme.colors.text, fontSize: '0.95rem', margin: 0 }}>Saldo actual: <strong style={{ color: saldoActual >= estimatedCost ? theme.colors.success : theme.colors.error }}>${Math.floor(saldoActual).toLocaleString('es-CO')} pesos</strong></p>
                </div>
                {modalError && <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>{modalError}</p>}
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button onClick={() => setModalStep(2)} disabled={submitting} style={{ flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '0.9rem' }}>No</button>
                  <button onClick={handleSend} disabled={submitting} style={{ flex: 1, padding: '10px', backgroundColor: submitting ? theme.colors.border : theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '0.9rem', fontWeight: '700' }}>
                    {submitting ? 'Enviando...' : 'Sí, enviar'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {insufficient && (
        <div onClick={() => setInsufficient(null)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `2px solid ${theme.colors.error}`, padding: '2rem', width: '100%', maxWidth: '460px', boxShadow: '0 8px 40px rgba(239,68,68,0.25)', textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.8rem' }}>⚠️</div>
            <h2 style={{ color: theme.colors.error, fontSize: '1.3rem', marginBottom: '1rem' }}>Saldo insuficiente</h2>
            <div style={{ backgroundColor: theme.colors.background, borderRadius: '10px', padding: '1rem', marginBottom: '1.3rem', textAlign: 'left' }}>
              <p style={{ color: theme.colors.text, fontSize: '0.92rem', margin: '0 0 0.5rem' }}>
                Actualmente tienes <strong style={{ color: theme.colors.primary }}>${insufficient.saldo.toLocaleString('es-CO')} pesos</strong> de saldo.
              </p>
              {insufficient.committedToday > 0 && (
                <p style={{ color: theme.colors.textMuted, fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
                  Ya comprometiste <strong>${insufficient.committedToday.toLocaleString('es-CO')}</strong> hoy en otras campañas, por lo que te quedan <strong>${insufficient.available.toLocaleString('es-CO')}</strong> disponibles.
                </p>
              )}
              <p style={{ color: theme.colors.text, fontSize: '0.92rem', margin: 0 }}>
                Esta campaña que vas a enviar vale <strong style={{ color: '#facc15' }}>${insufficient.cost.toLocaleString('es-CO')} pesos</strong>, no te alcanza.
              </p>
            </div>
            <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem', lineHeight: '1.6', marginBottom: '1.3rem' }}>
              Por eso no te permitimos avanzar. Si deseas enviarla, por favor recarga tu saldo en el siguiente enlace.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setInsufficient(null)} style={{ flex: 1, padding: '11px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, cursor: 'pointer', fontSize: '0.9rem' }}>Cancelar</button>
              <button onClick={() => navigate('/recargar')} style={{ flex: 1, padding: '11px', backgroundColor: theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, cursor: 'pointer', fontSize: '0.9rem', fontWeight: '700' }}>Recargar saldo ▶</button>
            </div>
          </div>
        </div>
      )}

      {leyHorario && (
        <div onClick={() => setLeyHorario(null)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: '2px solid #facc15', padding: '2rem', width: '100%', maxWidth: '480px', boxShadow: '0 8px 40px rgba(250,204,21,0.2)', textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.8rem' }}>🕒</div>
            <h2 style={{ color: '#facc15', fontSize: '1.25rem', marginBottom: '1rem' }}>Horario no permitido</h2>
            <p style={{ color: theme.colors.text, fontSize: '0.92rem', lineHeight: '1.6', marginBottom: '1.2rem' }}>
              Según la <strong>Ley 2300 de 2023 ("Dejen de Fregar")</strong>, no está permitido enviar comunicaciones comerciales o publicitarias en este horario.
            </p>
            <div style={{ backgroundColor: theme.colors.background, borderRadius: '10px', padding: '1rem 1.2rem', marginBottom: '1.3rem', textAlign: 'left' }}>
              <p style={{ color: theme.colors.textMuted, fontSize: '0.82rem', margin: '0 0 0.6rem', fontWeight: '700' }}>Horarios permitidos:</p>
              {(leyHorario.horarios || []).map((h, i) => (
                <p key={i} style={{ color: theme.colors.text, fontSize: '0.9rem', margin: '0 0 0.4rem' }}>• {h}</p>
              ))}
            </div>
            <button onClick={() => setLeyHorario(null)} style={{ width: '100%', padding: '11px', backgroundColor: theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, cursor: 'pointer', fontSize: '0.9rem', fontWeight: '700' }}>Entendido</button>
          </div>
        </div>
      )}
    </main>
  );
}

export default Marketing;
